/**
 * PropertyEligibilityPanel - the decision surface at step 2 of the
 * transfer wizard.
 *
 * Shows three things stacked:
 *   1. Property card - address + holder(s) + prior-transfer chip
 *   2. Eligibility card - state pill + days since closing + helper copy
 *   3. Transfer rules summary - 60 / 100 / exception thresholds, with the
 *      current state's threshold highlighted so the agent can see exactly
 *      where this transfer sits on the timeline.
 *
 * Prior transfer count is pulled from `holding.transfer_count` which is
 * incremented by the lifecycle service on approved + sale transfers
 * (`COUNT_INCREMENTING_TYPES` in backend/apps/transfers/services/lifecycle.py).
 */

import type { ResolvedAddress } from '@/admin/hooks/use-lookups'
import type { Holding, HoldingWarranty } from '@/admin/types/products'
import { Badge } from '@/shared/components/ui/badge'
import { cn } from '@/shared/lib/utils'
import {
  CheckCircle2,
  AlertTriangle,
  Ban,
  Clock,
  Home,
  RefreshCw,
} from 'lucide-react'
import {
  computeEligibility,
  type EligibilityState,
} from './eligibility'
import type {
  EligibilityPreflightResult,
} from '@/admin/hooks/use-transfers'
import { warrantyTypeLabel } from './warranty-label'

interface PropertyEligibilityPanelProps {
  hostAddress: ResolvedAddress | null
  hostDisplay?: string | null
  closingDate: string | null
  holdings: Holding[]
  /** Optional manual override when the caller knows the transfer was used. */
  transferUsed?: boolean
  /** The property's owner today, when the server knows it. */
  currentOwner?: { first_name: string; last_name: string } | null
  /** Server-authoritative preflight result from the wizard parent. */
  preflight: EligibilityPreflightResult | null
  /** True while the parent's preflight mutation is in flight. */
  preflightLoading?: boolean
}

// ── Pill styling per eligibility state ───────────────────

function StatePill({ state, label }: { state: EligibilityState; label: string }) {
  const config = {
    eligible: {
      classes: 'bg-[var(--status-ok-bg)] text-[var(--status-ok)]',
      Icon: CheckCircle2,
    },
    override: {
      classes: 'bg-[var(--status-warn-bg)] text-[var(--status-warn)]',
      Icon: AlertTriangle,
    },
    transfer_used: {
      classes: 'bg-[var(--bg-subtle)] text-[var(--ink-3)]',
      Icon: Ban,
    },
    no_closing_date: {
      classes: 'bg-[var(--bg-subtle)] text-[var(--ink-3)]',
      Icon: Clock,
    },
    unknown: {
      classes: 'bg-[var(--bg-subtle)] text-[var(--ink-3)]',
      Icon: Clock,
    },
  } as const

  const { classes, Icon } = config[state]

  return (
    <Badge
      variant="secondary"
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-medium uppercase tracking-[0.08em]',
        classes,
      )}
    >
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  )
}

// ── Helpers ──────────────────────────────────────────────

function pickPrimaryWarranty(holdings: Holding[]): HoldingWarranty | null {
  for (const h of holdings) {
    const w = h.warranties?.[0]
    if (w) return w
  }
  return null
}

// One transfer bumps every registration it covers, so the most-used one counts.
function priorTransfers(holdings: Holding[]): number {
  return Math.max(0, ...holdings.map((h) => h.transfer_count ?? 0))
}

function uniqueHolderNames(holdings: Holding[]): string[] {
  const seen = new Set<string>()
  const names: string[] = []
  for (const h of holdings) {
    const name = `${h.consumer_account?.first_name ?? ''} ${
      h.consumer_account?.last_name ?? ''
    }`.trim()
    if (name && !seen.has(name)) {
      seen.add(name)
      names.push(name)
    }
  }
  return names
}

function formatCoverageRemaining(expires: string | null): string {
  if (!expires) return 'Lifetime'
  const end = new Date(expires)
  const now = new Date()
  const msPerDay = 1000 * 60 * 60 * 24
  const daysLeft = Math.floor((end.getTime() - now.getTime()) / msPerDay)
  if (daysLeft < 0) return 'Expired'
  if (daysLeft < 365) return `${daysLeft} days remaining`
  const yearsLeft = (daysLeft / 365).toFixed(1)
  return `${yearsLeft} years remaining`
}

// ── Component ────────────────────────────────────────────

export function PropertyEligibilityPanel({
  hostAddress,
  hostDisplay,
  closingDate,
  holdings,
  transferUsed,
  currentOwner,
  preflight,
  preflightLoading = false,
}: PropertyEligibilityPanelProps) {
  const priorTransferCount = priorTransfers(holdings)
  const derivedTransferUsed =
    transferUsed !== undefined ? transferUsed : priorTransferCount > 0
  const eligibility = computeEligibility({
    closingDate,
    preflight,
    preflightLoading,
    transferUsed: derivedTransferUsed,
  })
  const primaryWarranty = pickPrimaryWarranty(holdings)
  const holdingCount = holdings.length
  const ownerName = currentOwner
    ? `${currentOwner.first_name} ${currentOwner.last_name}`.trim()
    : ''
  const holders = ownerName ? [ownerName] : uniqueHolderNames(holdings)

  return (
    <div className="flex flex-col gap-4">
      {/* ── Row 1: Property + Eligibility decision ─────────────── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Property Card */}
        <div
          className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-5"
          data-tour="transfer-property"
        >
          <div className="mb-3 flex items-center justify-between">
            <p className="clara-section-label flex items-center gap-1.5">
              <Home className="h-3 w-3" />
              Property
            </p>
            <Badge
              variant="secondary"
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-[0.08em]',
                priorTransferCount > 0
                  ? 'bg-[var(--status-warn-bg)] text-[var(--status-warn)]'
                  : 'bg-[var(--bg)] text-[var(--ink-3)]',
              )}
            >
              <RefreshCw className="h-3 w-3" />
              {priorTransferCount === 0
                ? 'No prior transfers'
                : priorTransferCount === 1
                  ? '1 prior transfer'
                  : `${priorTransferCount} prior transfers`}
            </Badge>
          </div>

          {hostAddress ? (
            <>
              <p className="text-[14px] text-[var(--ink)]">{hostAddress.line_1}</p>
              {hostAddress.line_2 && (
                <p className="text-[13px] text-[var(--ink-2)]">
                  {hostAddress.line_2}
                </p>
              )}
              {(hostAddress.town_or_city || hostAddress.county) && (
                <p className="text-[13px] text-[var(--ink-2)]">
                  {hostAddress.town_or_city}
                  {hostAddress.county ? `, ${hostAddress.county}` : ''}
                </p>
              )}
              {hostAddress.postcode && (
                <p className="text-[13px] text-[var(--ink-2)]">
                  {hostAddress.postcode}
                </p>
              )}
            </>
          ) : (
            <p className="text-[13px] text-[var(--ink-2)]">
              {hostDisplay ?? 'No property selected'}
            </p>
          )}

          <div className="mt-4 border-t-[0.5px] border-t-[var(--rule)] pt-4">
            <p className="text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
              Current owner
            </p>
            <p className="mt-1 text-[13px] text-[var(--ink)]">
              {holders.length === 0 ? '-' : holders.join(', ')}
            </p>
            <p className="mt-2 text-[11px] text-[var(--ink-3)]">
              {holdingCount}{' '}
              {holdingCount === 1
                ? 'warranty registration'
                : 'warranty registrations'}{' '}
              on this property
            </p>
          </div>
        </div>

        {/* Eligibility Card */}
        <div
          className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-5"
          data-tour="transfer-eligibility"
        >
          <div className="mb-3 flex items-center justify-between">
            <p className="clara-section-label">Transfer Eligibility</p>
            <StatePill state={eligibility.state} label={eligibility.label} />
          </div>

          <p className="mb-4 text-[12px] leading-relaxed text-[var(--ink-2)]">
            {eligibility.helperText}
          </p>

          {/* Data grid */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Days Since Closing
              </p>
              <p className="mt-0.5 text-[13px] text-[var(--ink)]">
                {eligibility.daysSinceClosing === null
                  ? '-'
                  : eligibility.daysSinceClosing < 0
                    ? `${Math.abs(eligibility.daysSinceClosing)} (future)`
                    : eligibility.daysSinceClosing}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Prior Transfers
              </p>
              <p className="mt-0.5 text-[13px] text-[var(--ink)]">
                {priorTransferCount}
              </p>
            </div>
            {primaryWarranty && (
              <>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                    Warranty Type
                  </p>
                  <p className="mt-0.5 text-[13px] text-[var(--ink)]">
                    {warrantyTypeLabel(primaryWarranty.warranty_type)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                    Coverage Remaining
                  </p>
                  <p className="mt-0.5 text-[13px] text-[var(--ink)]">
                    {formatCoverageRemaining(primaryWarranty.expires_at)}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Row 2: Transfer rules summary banner ──────────────── */}
      <TransferRulesBanner eligibility={eligibility} />
    </div>
  )
}

// ── Rule messages from the server ─────────────────────────
//
// Replaces the static "60 / 100 / exception" tier banner. All tenant-
// specific thresholds live in ``ValidationRule`` rows on the server;
// we surface whatever the preflight returned so the agent sees the
// exact rule message that fired.

function TransferRulesBanner({
  eligibility,
}: {
  eligibility: ReturnType<typeof computeEligibility>
}) {
  const allViolations = [
    ...eligibility.blockingViolations,
    ...eligibility.warnings,
  ]

  if (allViolations.length === 0) {
    if (eligibility.state === 'eligible') {
      return (
        <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-4">
          <p className="text-[12px] text-[var(--ink-3)]">
            All tenant-configured eligibility rules passed.
          </p>
        </div>
      )
    }
    return null
  }

  return (
    <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-5">
      <div className="mb-3 flex items-center justify-between">
        <p className="clara-section-label">Rule messages</p>
        <p className="text-[11px] text-[var(--ink-3)]">
          Evaluated against the tenant&rsquo;s configured rules
        </p>
      </div>
      <ul className="flex flex-col gap-2">
        {allViolations.map((v) => {
          const classes =
            v.severity === 'BLOCK'
              ? 'bg-[var(--status-error-bg)] text-[var(--status-error)]'
              : v.severity === 'WARN'
                ? 'bg-[var(--status-warn-bg)] text-[var(--status-warn)]'
                : 'bg-[var(--bg-subtle)] text-[var(--ink-3)]'
          return (
            <li
              key={v.rule_uuid}
              className="flex items-start gap-2 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-3"
            >
              <span
                className={cn(
                  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.06em]',
                  classes,
                )}
              >
                <span className="sr-only">Severity:</span>
                {v.severity}
              </span>
              <div className="min-w-0">
                <p className="text-[13px] text-[var(--ink)]">{v.name}</p>
                <p className="mt-0.5 text-[12px] text-[var(--ink-2)]">
                  {v.message}
                </p>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

